import { ref, readonly, onMounted } from 'vue'

const STORAGE_KEY = 'tb_ticket_admin_app_version'
const CHECK_INTERVAL_MS = 5 * 60 * 1000
const VERSION_ENDPOINT = '/meta/version.json'

const hasUpdateFlag = ref(false)
const currentVersion = ref<string | null>(null)
const latestVersion = ref<string | null>(null)
const checking = ref(false)
const isUpdating = ref(false)

let pollingStarted = false

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, { cache: 'no-cache' })
    if (!res.ok) return null
    const data = await res.json()
    return data.version || null
  } catch {
    return null
  }
}

function ensureStoredVersion(): void {
  const stored = localStorage.getItem(STORAGE_KEY)
  currentVersion.value = stored || '0.0.0'
}

async function checkForUpdateInternal(): Promise<void> {
  if (checking.value) return
  checking.value = true

  try {
    ensureStoredVersion()
    const latest = await fetchLatestVersion()

    if (latest) {
      latestVersion.value = latest
      hasUpdateFlag.value = latest !== currentVersion.value
    } else {
      hasUpdateFlag.value = false
    }
  } finally {
    checking.value = false
  }
}

async function clearServiceWorkerCache(): Promise<void> {
  if ('caches' in window) {
    const names = await caches.keys()
    await Promise.all(names.map((name) => caches.delete(name)))
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((r) => r.unregister()))
  }
}

function reloadApp(): void {
  isUpdating.value = true

  if (latestVersion.value) {
    localStorage.setItem(STORAGE_KEY, latestVersion.value)
    currentVersion.value = latestVersion.value
    hasUpdateFlag.value = false
  }

  clearServiceWorkerCache()
    .then(() => {
      setTimeout(() => {
        window.location.href = `${window.location.href.split('?')[0]}?_t=${Date.now()}`
      }, 3000)
    })
    .catch(() => {
      setTimeout(() => window.location.reload(), 3000)
    })
}

function dismissUpdate(): void {
  if (latestVersion.value) {
    localStorage.setItem(STORAGE_KEY, latestVersion.value)
    currentVersion.value = latestVersion.value
    hasUpdateFlag.value = false
  }
}

export function useAppUpdate() {
  onMounted(() => {
    if (pollingStarted) return
    pollingStarted = true
    void checkForUpdateInternal()
    window.setInterval(() => {
      void checkForUpdateInternal()
    }, CHECK_INTERVAL_MS)
  })

  return {
    hasUpdate: readonly(hasUpdateFlag),
    currentVersion: readonly(currentVersion),
    latestVersion: readonly(latestVersion),
    checking: readonly(checking),
    isUpdating: readonly(isUpdating),
    checkForUpdate: checkForUpdateInternal,
    reloadApp,
    dismissUpdate,
  }
}
